import React, { useCallback, useEffect, useRef } from 'react';
import HumanIcon from '@uber/base-icons/accessibility';
import ArrowRotateLeft from '@uber/base-icons/arrow-rotate-left';
import useShortcut from '@uber/label-modalities/src/image/labelling/providers/hooks/use-shortcut';
import { styled, useStyletron } from 'baseui';
import { useAtomValue, useSetAtom } from 'jotai';
import _isEmpty from 'lodash/isEmpty';
import ToolbarButton, { TOOLBAR_BUTTON_SIZE } from '../../../../common/toolbar-button';
import { EVENTS } from '../../../labelling-v2/atoms/label-store-dispatch/events';
import { useStageAtoms } from '../../../labelling-v2/components/stage-scope/hooks';
import { SKELETON_TOOLS } from '../../constants';
import { ANNOTATION_TYPE } from '../../constants';
import useStage from '../../providers/hooks/use-stage';
import { handleShortcutRegistrationError } from '../../providers/utils/shortcut';
import type { SkeletonAnnotationT } from '../../types';
import SkeletonIcon from '../annotations/skeleton/config/icon';
import useAnalyticsForToolBar from './analytics/useAnalyticsForToolBar';
import PanelContainer from './panel-container';
const SKELETON_PANEL_MAX_WIDTH = 110;
const Icons = styled('div', {
  display: 'flex',
  justifyContent: 'flex-start',
  marginTop: '10px'
});
const SkeletonPanel = () => {
  const {
    brushToolSettingsAtom,
    isLoadingAtom,
    selectedAnnotationIdsAtom,
    currentFrameDataAtom,
    dispatchAtom,
    currentFrameAnnotationUnderUserReviewAtom
  } = useStageAtoms();
  const {
    selectedTool
  } = useAtomValue(brushToolSettingsAtom);
  const isToolLoading = useAtomValue(isLoadingAtom);
  const annotationUnderUserReview = useAtomValue(currentFrameAnnotationUnderUserReviewAtom);
  const selectedAnnotationIds = useAtomValue(selectedAnnotationIdsAtom);
  const frameData = useAtomValue(currentFrameDataAtom);
  const dispatchAction = useSetAtom(dispatchAtom);
  const [_, theme] = useStyletron();
  const {
    sendEventToolSelected
  } = useAnalyticsForToolBar();
  const {
    registerHotKeys
  } = useShortcut();
  const hasSetInitialTool = useRef(false);
  const {
    updateAnnotation
  } = useStage();
  const isToolDisabled = isToolLoading || !_isEmpty(annotationUnderUserReview);
  const hasSelectedSkeletonAnnotation = selectedAnnotationIds.some(annotationId => frameData?.annotationStore[annotationId]?.annotationType === ANNOTATION_TYPE.SKELETON);
  const isFlipDisabled = isToolDisabled || !hasSelectedSkeletonAnnotation;
  const onFreeHandSkeletonClick = useCallback(() => {
    dispatchAction({
      type: EVENTS.SET_BRUSH_TOOL_SETTINGS,
      brushToolSettings: {
        selectedTool: SKELETON_TOOLS.FREE_HAND_SKELETON,
        eraserSelected: false
      }
    });
    sendEventToolSelected({
      tool: SKELETON_TOOLS.FREE_HAND_SKELETON
    });
  }, [dispatchAction, sendEventToolSelected]);
  const onSecondSkeletonToolClick = useCallback(() => {
    dispatchAction({
      type: EVENTS.SET_BRUSH_TOOL_SETTINGS,
      brushToolSettings: {
        selectedTool: SKELETON_TOOLS.HUMAN_SKELETON_TOOL,
        eraserSelected: false
      }
    });
    sendEventToolSelected({
      tool: SKELETON_TOOLS.HUMAN_SKELETON_TOOL
    });
  }, [dispatchAction, sendEventToolSelected]);
  const flipHumanSkeleton = useCallback(() => {
    const selectedSkeletonId = selectedAnnotationIds.find(annotationId => frameData?.annotationStore[annotationId]?.annotationType === ANNOTATION_TYPE.SKELETON);
    if (!selectedSkeletonId) {
      return;
    }
    const annotation = (frameData?.annotationStore[selectedSkeletonId] as SkeletonAnnotationT);
    if (!annotation) {
      return;
    }
    const nodes = annotation.data.nodes;
    const xCoordinates = nodes.map(node => node.point[0]);
    const minX = Math.min(...xCoordinates);
    const maxX = Math.max(...xCoordinates);
    const centerX = (minX + maxX) / 2;
    const flippedNodes = nodes.map(node => {
      const point: [number, number] = [centerX + (centerX - node.point[0]), node.point[1]];
      return {
        ...node,
        point
      };
    });
    const flippedAnnotation: SkeletonAnnotationT = {
      ...annotation,
      data: {
        ...annotation.data,
        nodes: flippedNodes
      }
    };
    updateAnnotation(selectedSkeletonId, flippedAnnotation);
    sendEventToolSelected({
      tool: 'FLIP_HUMAN_SKELETON'
    });
  }, [selectedAnnotationIds, frameData, updateAnnotation, sendEventToolSelected]);
  useEffect(() => {
    const {
      errors,
      removeHotKeys
    } = registerHotKeys([{
      keys: ['w'],
      onAction: onFreeHandSkeletonClick
    }, {
      keys: ['q'],
      onAction: onSecondSkeletonToolClick
    }, {
      keys: ['t'],
      onAction: flipHumanSkeleton
    }]);
    errors.forEach(error => handleShortcutRegistrationError(error));
    return () => {
      removeHotKeys();
    };
  }, [onFreeHandSkeletonClick, registerHotKeys, onSecondSkeletonToolClick, flipHumanSkeleton]);
  useEffect(() => {
    // Set default skeleton tool when skeleton panel first mounts
    // Only set once and only if no skeleton tool is currently selected
    if (!hasSetInitialTool.current && selectedTool !== SKELETON_TOOLS.FREE_HAND_SKELETON && selectedTool !== SKELETON_TOOLS.HUMAN_SKELETON_TOOL) {
      dispatchAction({
        type: EVENTS.SET_BRUSH_TOOL_SETTINGS,
        brushToolSettings: {
          selectedTool: SKELETON_TOOLS.FREE_HAND_SKELETON,
          eraserSelected: false
        }
      });
      sendEventToolSelected({
        tool: SKELETON_TOOLS.FREE_HAND_SKELETON
      });
      hasSetInitialTool.current = true;
    }
  }, [selectedTool, dispatchAction, sendEventToolSelected, hasSelectedSkeletonAnnotation]);
  return <PanelContainer width={SKELETON_PANEL_MAX_WIDTH} data-testid="skeleton-panel">
      <Icons>
        <ToolbarButton title="Free Hand Skeleton" onClick={onFreeHandSkeletonClick} attributes={{
        'data-testid': 'free-hand-skeleton'
      }} isSelected={selectedTool === SKELETON_TOOLS.FREE_HAND_SKELETON} size={TOOLBAR_BUTTON_SIZE.SMALL} disabled={isToolDisabled}>
          <SkeletonIcon strokeColor={theme.colors.primary} />
        </ToolbarButton>
        <ToolbarButton title="Human Skeleton" onClick={onSecondSkeletonToolClick} attributes={{
        'data-testid': 'human-skeleton'
      }} isSelected={selectedTool === SKELETON_TOOLS.HUMAN_SKELETON_TOOL} size={TOOLBAR_BUTTON_SIZE.SMALL} disabled={isToolDisabled}>
          <HumanIcon size={16} />
        </ToolbarButton>
        <ToolbarButton title="Flip Skeleton" onClick={flipHumanSkeleton} attributes={{
        'data-testid': 'flip-human-skeleton'
      }} isSelected={false} size={TOOLBAR_BUTTON_SIZE.SMALL} disabled={isFlipDisabled}>
          <ArrowRotateLeft size={16} />
        </ToolbarButton>
      </Icons>
    </PanelContainer>;
};
export default SkeletonPanel;